import React, { useState, useEffect } from 'react';
import { Typography, Card, Switch, Space, message, Spin, Row, Col, InputNumber, Tabs, Table, Empty, Button, Tag, Modal, Input, Alert, Select } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import api from '../services/api';
import { notificationService } from '../services/notifications';
import { documentService } from '../services/documents';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/MainLayout';
import dayjs from 'dayjs';

const { Title, Paragraph } = Typography;

function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [notificationLogs, setNotificationLogs] = useState([]);
  const [notificationLogsLoading, setNotificationLogsLoading] = useState(false);
  const [logsPageSize] = useState(20);
  const [logsSummary, setLogsSummary] = useState(null);
  const [testEmailModalOpen, setTestEmailModalOpen] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null); // {success, message}
  const [expirationTestModalOpen, setExpirationTestModalOpen] = useState(false);
  const [expirationTestDocuments, setExpirationTestDocuments] = useState([]);
  const [expirationTestDocumentsLoading, setExpirationTestDocumentsLoading] = useState(false);
  const [expirationTestDocumentId, setExpirationTestDocumentId] = useState(null);
  const [expirationTestRecipient, setExpirationTestRecipient] = useState('');
  const [expirationTestSending, setExpirationTestSending] = useState(false);
  const [expirationTestResult, setExpirationTestResult] = useState(null);

  const formatError = (error, fallback = 'Errore sconosciuto') => {
    const detail = error?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((item) => item?.msg || item?.detail || JSON.stringify(item)).join(' | ');
    }
    if (typeof detail === 'object') return detail.msg || detail.detail || JSON.stringify(detail);
    return fallback;
  };

  useEffect(() => {
    fetchEmailSetting();
    fetchNotificationSettings();
  }, []);

  const fetchEmailSetting = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/email_enabled');
      const value = response.data.value.toLowerCase() === 'true';
      setEmailEnabled(value);
    } catch (error) {
      // If setting doesn't exist yet, default to true
      if (error.response?.status === 404) {
        setEmailEnabled(true);
      } else {
        message.error('Errore nel caricamento delle impostazioni');
        console.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailToggle = async (checked) => {
    try {
      await api.put('/settings/email_enabled', {
        value: String(checked),
        description: 'Enable or disable email notifications for the platform'
      });
      setEmailEnabled(checked);
      message.success(`Email ${checked ? 'abilitata' : 'disabilitata'}`);
    } catch (error) {
      message.error('Errore nell\'aggiornamento delle impostazioni');
      console.error(error);
      // Revert on error
      setEmailEnabled(!checked);
    }
  };

  const fetchNotificationSettings = async () => {
    try {
      const data = await notificationService.getSettings();
      setNotificationSettings(data);
    } catch (error) {
      console.error('Errore nel caricamento impostazioni notifiche:', error);
      // Default values - notifications DISABLED by default
      setNotificationSettings({
        notifications_enabled: false,
        notification_days_first: 7,
        notification_days_second: 3,
        notification_days_third: 1,
      });
    }
  };

  const handleNotificationSettingsChange = async (field, value) => {
    try {
      const updatedSettings = { ...notificationSettings, [field]: value };
      const updated = await notificationService.updateSettings(updatedSettings);
      setNotificationSettings(updated);
      message.success('Impostazioni notifiche aggiornate');
    } catch (error) {
      message.error('Errore aggiornamento impostazioni');
      console.error(error);
    }
  };

  const fetchNotificationLogs = async () => {
    try {
      setNotificationLogsLoading(true);
      const logs = await api.get('/email/logs', { params: { limit: logsPageSize } });
      setNotificationLogs(logs.data);
      setLogsSummary({ total_notifications_sent: logs.data.length });
    } catch (error) {
      message.error('Errore caricamento log email');
      console.error(error);
    } finally {
      setNotificationLogsLoading(false);
    }
  };

  const handleManualTrigger = async () => {
    try {
      setNotificationLogsLoading(true);
      const result = await notificationService.triggerManualProcessing();
      if (result.status === 'error') {
        message.error(`Errore: ${result.error}`);
      } else {
        message.success(`Notifiche elaborate: ${result.notifications_sent} inviate${result.notifications_failed > 0 ? `, ${result.notifications_failed} non riuscite` : ''}`);
        await fetchNotificationLogs();
      }
    } catch (error) {
      message.error('Errore nel trigger manuale');
      console.error(error);
    } finally {
      setNotificationLogsLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmailAddress || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmailAddress)) {
      message.error('Inserisci un indirizzo email valido');
      return;
    }
    try {
      setTestEmailSending(true);
      setTestEmailResult(null);
      await api.post('/email/send-test', { to_email: testEmailAddress });
      setTestEmailResult({ success: true, message: `Email di test inviata a ${testEmailAddress}` });
    } catch (error) {
      const errMsg = formatError(error, 'Errore sconosciuto durante l\'invio');
      setTestEmailResult({ success: false, message: errMsg });
    } finally {
      setTestEmailSending(false);
    }
  };

  const getItemLabel = (item) => {
    const fields = item?.standard_fields || {};
    const type = item?.item_type;
    if (type === 'worker') return `${fields.first_name || ''} ${fields.last_name || ''}`.trim() || fields.fiscal_code || `Lavoratore #${item.id}`;
    if (type === 'vehicle') return fields.identifier || `Veicolo #${item.id}`;
    if (type === 'equipment') return fields.serial_number || `Attrezzatura #${item.id}`;
    if (type === 'chemical') return fields.name || `Prodotto chimico #${item.id}`;
    if (type === 'company') return fields.name || fields.code || `Azienda #${item.id}`;
    return item ? `Elemento #${item.id}` : '';
  };

  const getDocumentOptionLabel = (doc) => {
    const itemLabel = getItemLabel(doc.item);
    const expiration = doc.expiration_date ? dayjs(doc.expiration_date).format('DD/MM/YYYY') : 'senza scadenza';
    return `${doc.document_type}${itemLabel ? ` - ${itemLabel}` : ''} - scade ${expiration}`;
  };

  const loadExpirationTestDocuments = async () => {
    try {
      setExpirationTestDocumentsLoading(true);
      const docs = await documentService.list({ limit: 5000 });
      const docsWithExpiration = (docs || [])
        .filter((doc) => !!doc.expiration_date)
        .sort((a, b) => dayjs(a.expiration_date).valueOf() - dayjs(b.expiration_date).valueOf());
      setExpirationTestDocuments(docsWithExpiration);
      if (!expirationTestDocumentId && docsWithExpiration.length === 1) {
        setExpirationTestDocumentId(docsWithExpiration[0].id);
      }
    } catch (error) {
      message.error(formatError(error, 'Errore caricamento documenti con scadenza'));
    } finally {
      setExpirationTestDocumentsLoading(false);
    }
  };

  const openExpirationTestModal = async () => {
    setExpirationTestResult(null);
    setExpirationTestRecipient(user?.email || '');
    setExpirationTestModalOpen(true);
    if (expirationTestDocuments.length === 0) {
      await loadExpirationTestDocuments();
    }
  };

  const handleSendExpirationTest = async () => {
    if (!expirationTestDocumentId) {
      message.error('Seleziona un documento con scadenza');
      return;
    }
    if (!expirationTestRecipient || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(expirationTestRecipient)) {
      message.error('Inserisci un indirizzo email valido');
      return;
    }
    try {
      setExpirationTestSending(true);
      setExpirationTestResult(null);
      await documentService.sendExpirationTest(expirationTestDocumentId, expirationTestRecipient);
      setExpirationTestResult({ success: true, message: `Email scadenza inviata a ${expirationTestRecipient}` });
      await fetchNotificationLogs();
    } catch (error) {
      setExpirationTestResult({
        success: false,
        message: formatError(error, 'Errore sconosciuto durante l\'invio della simulazione'),
      });
    } finally {
      setExpirationTestSending(false);
    }
  };

  const renderExpirationTestAction = () => {
    if (!isAdmin) return null;

    return (
      <div style={{
        padding: '16px',
        border: '1px solid #f0f0f0',
        borderRadius: '4px'
      }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <div>
              <Title level={4} style={{ marginBottom: '8px' }}>Simula scadenza documento</Title>
              <Paragraph style={{ marginBottom: 0, color: '#666' }}>
                Invia una mail di avviso scadenza scegliendo un documento con data di scadenza
              </Paragraph>
            </div>
          </Col>
          <Col>
            <Button
              icon={<MailOutlined />}
              onClick={openExpirationTestModal}
            >
              Simula scadenza
            </Button>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <MainLayout title="Impostazioni">
      <Spin spinning={loading}>
        <Tabs
          items={[
            {
              key: 'general',
              label: 'Generali',
              children: (
                <Card>
                  <Title level={3}>Impostazioni Piattaforma</Title>
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div style={{ 
                      padding: '16px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '4px'
                    }}>
                      <Row justify="space-between" align="middle">
                        <Col>
                          <div>
                            <Title level={4} style={{ marginBottom: '8px' }}>Invio email globale</Title>
                            <Paragraph style={{ marginBottom: 0, color: '#666' }}>
                              Abilita o disabilita l'invio di tutte le email: inviti, reset password, test e notifiche scadenze
                            </Paragraph>
                          </div>
                        </Col>
                        <Col>
                          <Switch 
                            checked={emailEnabled}
                            onChange={handleEmailToggle}
                            checkedChildren="Abilitata"
                            unCheckedChildren="Disabilitata"
                            size="large"
                          />
                        </Col>
                      </Row>
                    </div>
                    {isAdmin && (
                      <div style={{
                        padding: '16px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '4px'
                      }}>
                        <Row justify="space-between" align="middle" gutter={[16, 16]}>
                          <Col>
                            <div>
                              <Title level={4} style={{ marginBottom: '8px' }}>Email di test</Title>
                              <Paragraph style={{ marginBottom: 0, color: '#666' }}>
                                Invia una mail di prova a un indirizzo scelto per verificare la configurazione SMTP
                              </Paragraph>
                            </div>
                          </Col>
                          <Col>
                            <Button
                              type="primary"
                              icon={<MailOutlined />}
                              onClick={() => {
                                setTestEmailResult(null);
                                setTestEmailModalOpen(true);
                              }}
                            >
                              Invia test
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    )}
                    {renderExpirationTestAction()}
                  </Space>
                </Card>
              ),
            },
            {
              key: 'notifications',
              label: 'Notifiche Scadenze',
              children: notificationSettings ? (
                <Card>
                  <Title level={3}>Gestione Notifiche Scadenza Documenti</Title>
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div style={{ 
                      padding: '16px',
                      border: '1px solid #f0f0f0',
                      borderRadius: '4px'
                    }}>
                      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                        <Col>
                          <div>
                            <Title level={4} style={{ marginBottom: '8px' }}>Notifiche scadenze automatiche</Title>
                            <Paragraph style={{ marginBottom: 0, color: '#666' }}>
                              Abilita o disabilita solo il controllo schedulato che invia le email per documenti in scadenza
                            </Paragraph>
                          </div>
                        </Col>
                        <Col>
                          <Switch 
                            checked={notificationSettings.notifications_enabled}
                            onChange={(checked) => handleNotificationSettingsChange('notifications_enabled', checked)}
                            checkedChildren="Abilitate"
                            unCheckedChildren="Disabilitate"
                            size="large"
                          />
                        </Col>
                      </Row>
                    </div>

                    {renderExpirationTestAction()}

                    {notificationSettings.notifications_enabled && (
                      <>
                        <div style={{ 
                          padding: '16px',
                          border: '1px solid #f0f0f0',
                          borderRadius: '4px'
                        }}>
                          <Title level={4} style={{ marginBottom: '24px' }}>Giorni di allerta</Title>
                          <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Row gutter={16}>
                              <Col span={8}>
                                <div>
                                  <Paragraph style={{ fontWeight: 'bold', marginBottom: '8px' }}>Prima notifica (giorni)</Paragraph>
                                  <InputNumber
                                    min={1}
                                    max={365}
                                    value={notificationSettings.notification_days_first}
                                    onChange={(val) => handleNotificationSettingsChange('notification_days_first', val)}
                                    style={{ width: '100%' }}
                                  />
                                </div>
                              </Col>
                              <Col span={8}>
                                <div>
                                  <Paragraph style={{ fontWeight: 'bold', marginBottom: '8px' }}>Seconda notifica (giorni)</Paragraph>
                                  <InputNumber
                                    min={1}
                                    max={365}
                                    value={notificationSettings.notification_days_second}
                                    onChange={(val) => handleNotificationSettingsChange('notification_days_second', val)}
                                    style={{ width: '100%' }}
                                  />
                                </div>
                              </Col>
                              <Col span={8}>
                                <div>
                                  <Paragraph style={{ fontWeight: 'bold', marginBottom: '8px' }}>Terza notifica (giorni)</Paragraph>
                                  <InputNumber
                                    min={1}
                                    max={365}
                                    value={notificationSettings.notification_days_third}
                                    onChange={(val) => handleNotificationSettingsChange('notification_days_third', val)}
                                    style={{ width: '100%' }}
                                  />
                                </div>
                              </Col>
                            </Row>
                          </Space>
                        </div>

                        <div style={{ 
                          padding: '12px 16px',
                          backgroundColor: '#e6f7ff',
                          borderRadius: '4px',
                          borderLeft: '4px solid #1890ff'
                        }}>
                          <Paragraph style={{ marginBottom: 0, fontSize: '12px', color: '#0050b3' }}>
                            💡 Le notifiche verranno inviate all'email associata all'azienda proprietaria del documento quando i giorni rimasti equivalgono ai valori configurati sopra.
                          </Paragraph>
                        </div>
                      </>
                    )}
                  </Space>
                </Card>
              ) : null,
            },
            {
              key: 'logs',
              label: 'Log Email',
              children: (
                <Card>
                  <Title level={3}>Log Email Inviati</Title>
                  <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
                      <Button
                        type="primary"
                        onClick={fetchNotificationLogs}
                        loading={notificationLogsLoading}
                      >
                        Ricarica Log
                      </Button>
                      {isAdmin && (
                        <Button
                          onClick={handleManualTrigger}
                          loading={notificationLogsLoading}
                        >
                          ▶ Elabora notifiche ora
                        </Button>
                      )}
                    </div>

                    {logsSummary && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        marginBottom: '16px'
                      }}>
                        <Card size="small" style={{ textAlign: 'center' }}>
                          <Title level={5} style={{ marginBottom: '8px' }}>Totale Email</Title>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1890ff' }}>
                            {logsSummary.total_notifications_sent}
                          </div>
                        </Card>
                      </div>
                    )}

                    <Table
                      loading={notificationLogsLoading}
                      dataSource={notificationLogs}
                      columns={[
                        {
                          title: 'Data Invio',
                          dataIndex: 'sent_at',
                          key: 'sent_at',
                          width: 160,
                          render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
                        },
                        {
                          title: 'Tipo',
                          dataIndex: 'email_type',
                          key: 'email_type',
                          width: 170,
                          render: (type) => {
                            const map = {
                              expiration_notification: { color: 'orange', label: 'Scadenza Doc.' },
                              invitation: { color: 'blue', label: 'Invito Utente' },
                              password_reset: { color: 'purple', label: 'Reset Password' },
                              forgot_password: { color: 'purple', label: 'Password Dimenticata' },
                              test: { color: 'cyan', label: 'Test' },
                              generic: { color: 'default', label: 'Generica' },
                            };
                            const info = map[type] || { color: 'default', label: type };
                            return <Tag color={info.color}>{info.label}</Tag>;
                          },
                        },
                        {
                          title: 'Destinatario',
                          dataIndex: 'recipient_email',
                          key: 'recipient_email',
                          width: 220,
                        },
                        {
                          title: 'Oggetto',
                          dataIndex: 'subject',
                          key: 'subject',
                          ellipsis: true,
                        },
                        {
                          title: 'Esito',
                          dataIndex: 'success',
                          key: 'success',
                          width: 90,
                          render: (s) => s
                            ? <Tag color="green">OK</Tag>
                            : <Tag color="red">Errore</Tag>,
                        },
                      ]}
                      rowKey={(record) => `${record.id}`}
                      pagination={{ pageSize: logsPageSize }}
                      locale={{ emptyText: <Empty description="Nessun log disponibile" /> }}
                    />
                  </Space>
                </Card>
              ),
            },
          ]}
        />

        <Modal
          title="Invia email di test"
          open={testEmailModalOpen}
          onCancel={() => {
            setTestEmailModalOpen(false);
            setTestEmailResult(null);
          }}
          onOk={handleSendTestEmail}
          confirmLoading={testEmailSending}
          okText="Invia"
          cancelText="Annulla"
          destroyOnHidden
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Input
              type="email"
              value={testEmailAddress}
              onChange={(event) => setTestEmailAddress(event.target.value)}
              onPressEnter={handleSendTestEmail}
              placeholder="destinatario@example.com"
              prefix={<MailOutlined />}
            />
            {testEmailResult && (
              <Alert
                type={testEmailResult.success ? 'success' : 'error'}
                showIcon
                message={testEmailResult.success ? 'Invio completato' : 'Invio non riuscito'}
                description={testEmailResult.message}
              />
            )}
          </Space>
        </Modal>

        <Modal
          title="Simula email scadenza documento"
          open={expirationTestModalOpen}
          onCancel={() => {
            setExpirationTestModalOpen(false);
            setExpirationTestResult(null);
          }}
          onOk={handleSendExpirationTest}
          confirmLoading={expirationTestSending}
          okText="Invia simulazione"
          cancelText="Annulla"
          destroyOnHidden
          width={640}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Select
              showSearch
              value={expirationTestDocumentId}
              loading={expirationTestDocumentsLoading}
              placeholder="Seleziona documento con scadenza"
              style={{ width: '100%' }}
              optionFilterProp="label"
              onDropdownVisibleChange={(open) => {
                if (open) loadExpirationTestDocuments();
              }}
              onChange={setExpirationTestDocumentId}
              options={expirationTestDocuments.map((doc) => ({
                value: doc.id,
                label: getDocumentOptionLabel(doc),
              }))}
              notFoundContent={expirationTestDocumentsLoading ? 'Caricamento...' : 'Nessun documento con scadenza'}
            />
            <Input
              type="email"
              value={expirationTestRecipient}
              onChange={(event) => setExpirationTestRecipient(event.target.value)}
              onPressEnter={handleSendExpirationTest}
              placeholder="destinatario@example.com"
              prefix={<MailOutlined />}
            />
            {expirationTestResult && (
              <Alert
                type={expirationTestResult.success ? 'success' : 'error'}
                showIcon
                message={expirationTestResult.success ? 'Simulazione inviata' : 'Simulazione non riuscita'}
                description={expirationTestResult.message}
              />
            )}
          </Space>
        </Modal>
      </Spin>
    </MainLayout>
  );
}

export default Settings;
